import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo, useId } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import { exportChart } from '../../../utilities/ExportChart';
import EmptyState from '../../../components/empty-state/EmptyState';
import FullScreenIcon from '../../common/FullScreenIcon/FullScreenIcon';
import DownloadChart from '../../../components/time-series-chart/download/downloadChart';
import './RunlifeBarChart.scss';

interface SeriesConfigItem {
  key: string;
  name: string;
  color: string;
  selected?: boolean;
}

interface RunlifeBarChartProps {
  chartData: any[];
  seriesConfig: SeriesConfigItem[];
  widgetHeader: string;
  chartLabel: string;
  categoryField?: string;
  marginRight?: boolean;
}

const RunlifeBarChart: React.FC<RunlifeBarChartProps> = ({
  chartData,
  seriesConfig,
  widgetHeader = '',
  chartLabel = '',
  categoryField = 'Field',
  marginRight = false,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const am5RootRef = useRef<am5.Root | null>(null);
  const uniqueId = useId();
  const chartContentId = `runlife-bar-chart-content-${uniqueId}`;

  const hasData = Array.isArray(chartData) && chartData.length > 0;

  // Memoize calculations
  const { maxValue, dataWithUniqueKeys } = useMemo(() => {
    if (!hasData) return { maxValue: 1000, dataWithUniqueKeys: [] };

    const maxVal = Math.max(...chartData.flatMap((item) => seriesConfig.map((cfg) => item[cfg.key] || 0)));
    const uniqueData = chartData.map((item, index) => ({
      ...item,
      uniqueCategory: `${item[categoryField]}_${index}`,
      displayName: item[categoryField],
    }));

    return {
      maxValue: Math.ceil(maxVal / 200) * 200,
      dataWithUniqueKeys: uniqueData,
    };
  }, [chartData, hasData, seriesConfig, categoryField]);

  // Common style configurations
  const styleConfig = useMemo(
    () => ({
      commonAxis: { strokeWidth: 1, strokeOpacity: 1, stroke: am5.color('#4A5463') },
      grid: {
        stroke: am5.color('#8B93A1'),
        strokeWidth: 1,
        strokeDasharray: [3, 3],
        strokeOpacity: 0.7,
        visible: true,
      },
      labels: {
        fontSize: 12,
        fontFamily: 'Mulish',
        fontWeight: '400' as const,
      },
      tooltip: {
        background: am5.color('#001023'),
        text: am5.color('#FFFFFF'),
      },
    }),
    [],
  );

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleChartDownload = useCallback(
    async (option: string) => {
      const element = document.getElementById(chartContentId);
      if (!element) return;
      const type = option === 'Download as PDF' ? 'PDF' : 'JPG';
      await exportChart(element, type, widgetHeader, undefined, undefined, widgetHeader);
    },
    [widgetHeader, chartContentId],
  );

  const createAxis = useCallback(
    (root: am5.Root, chart: am5xy.XYChart) => {
      // Y-axis
      const yAxisRenderer = am5xy.AxisRendererY.new(root, {
        ...styleConfig.commonAxis,
        minGridDistance: 1,
        inversed: true,
        cellStartLocation: 0.15,
        cellEndLocation: 0.88,
      });
      const yAxis = chart.yAxes.push(
        am5xy.CategoryAxis.new(root, { categoryField: 'uniqueCategory', renderer: yAxisRenderer }),
      );

      // Customize labels to show only the field name, not the unique key
      yAxisRenderer.labels.template.setAll({
        ...styleConfig.labels,
        maxWidth: 120,
        oversizedBehavior: 'truncate',
        centerY: am5.p50,
        centerX: am5.p100,
        location: 0.5,
        populateText: true,
      });

      yAxisRenderer.labels.template.adapters.add('text', (text, target) => {
        const dataItem = target.dataItem;
        if (dataItem) {
          const dataContext = dataItem.dataContext as any;
          return dataContext?.displayName || text;
        }
        return text;
      });

      yAxis.children.unshift(
        am5.Label.new(root, {
          rotation: -90,
          y: am5.percent(50),
          centerX: am5.percent(50),
          textAlign: 'center',
          ...styleConfig.labels,
          marginRight: 15,
        }),
      );

      // X-axis
      const xAxisRenderer = am5xy.AxisRendererX.new(root, { ...styleConfig.commonAxis, minGridDistance: 50 });
      const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, { renderer: xAxisRenderer, min: 0, max: maxValue }));

      xAxisRenderer.labels.template.setAll(styleConfig.labels);
      xAxis.children.moveValue(
        am5.Label.new(root, {
          x: am5.percent(50),
          centerX: am5.percent(50),
          ...styleConfig.labels,
          marginTop: 10,
        }),
        xAxis.children.length - 1,
      );

      // Grid
      yAxis.get('renderer').grid.template.setAll(styleConfig.grid);
      xAxis.get('renderer').grid.template.setAll({ visible: false });

      yAxis.data.setAll(dataWithUniqueKeys);
      return { xAxis, yAxis };
    },
    [styleConfig, maxValue, dataWithUniqueKeys],
  );

  const createSeries = useCallback(
    (
      root: am5.Root,
      chart: am5xy.XYChart,
      xAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
      yAxis: am5xy.CategoryAxis<am5xy.AxisRenderer>,
    ) => {
      return seriesConfig
        .filter((cfg) => cfg.selected !== false)
        .map((cfg) => {
          const series = chart.series.push(
            am5xy.ColumnSeries.new(root, {
              name: cfg.name,
              xAxis,
              yAxis,
              valueXField: cfg.key,
              categoryYField: 'uniqueCategory',
              fill: am5.color(cfg.color),
              stroke: am5.color(cfg.color),
            }),
          );

          series.columns.template.setAll({
            cornerRadiusTR: 3,
            cornerRadiusBR: 3,
            height: am5.percent(65),
            strokeWidth: 1,
            stroke: am5.color(cfg.color),
            fill: am5.color(cfg.color),
          });

          series.set(
            'tooltip',
            am5.Tooltip.new(root, {
              labelText: ` {valueX} days`,
              pointerOrientation: 'left',
              getFillFromSprite: false,
              getStrokeFromSprite: false,
              autoTextColor: false,
            }),
          );

          const tooltip = series.get('tooltip');
          if (tooltip) {
            tooltip.get('background')?.setAll({
              fill: styleConfig.tooltip.background,
              fillOpacity: 1,
              stroke: styleConfig.tooltip.background,
            });
            tooltip.label.setAll({
              fill: styleConfig.tooltip.text,
              ...styleConfig.labels,
              fontSize: 14,
            });
          }

          series.data.setAll(dataWithUniqueKeys);
          return series;
        });
    },
    [seriesConfig, styleConfig, dataWithUniqueKeys],
  );

  const createLegend = useCallback(
    (root: am5.Root, chart: am5xy.XYChart, allSeries: am5xy.ColumnSeries[]) => {
      const legend = chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.percent(50),
          x: am5.percent(50),
          marginTop: 15,
          marginBottom: 5,
        }),
      );

      const legendConfig = { ...styleConfig.labels, fill: am5.color('#fff') };
      legend.labels.template.setAll(legendConfig);
      legend.valueLabels.template.setAll(legendConfig);
      legend.markers.template.setAll({ width: 12, height: 12 });

      const markerTemplate = legend.markerRectangles.template;
      markerTemplate.setAll({
        cornerRadiusTL: 50,
        cornerRadiusTR: 50,
        cornerRadiusBL: 50,
        cornerRadiusBR: 50,
        strokeWidth: 2,
      });

      legend.labels.template.adapters.add('text', (_, target) => (target.dataItem?.dataContext as any)?.name ?? '');
      markerTemplate.adapters.add('fill', (fill, target) => (target.dataItem?.dataContext as any)?.color ?? fill);
      markerTemplate.adapters.add('fillOpacity', (_, target) =>
        (target.dataItem?.dataContext as any)?.visible === false ? 0 : 1,
      );
      markerTemplate.adapters.add('stroke', (stroke, target) => (target.dataItem?.dataContext as any)?.color ?? stroke);

      legend.itemContainers.template.events.on('click', (ev) => {
        const legendData = ev.target.dataItem?.dataContext as any;
        if (!legendData) return;

        const isVisible = legendData.visible !== false;
        legendData.visible = !isVisible;

        allSeries.forEach((series) => {
          if (series.get('name') === legendData.name) {
            series.set('visible', !isVisible);
          }
        });

        const legendItem = legend.dataItems.find((item) => item.dataContext === legendData);
        if (legendItem) {
          const marker = legendItem.get('marker')?.children.getIndex(0) as am5.RoundedRectangle;
          marker?.set('fillOpacity', !isVisible ? 1 : 0);
        }
      });

      legend.itemContainers.template.set('cursorOverStyle', 'pointer');

      const legendData = seriesConfig
        .filter((cfg) => cfg.selected !== false)
        .map((cfg) => ({
          name: cfg.name,
          color: am5.color(cfg.color),
          visible: true,
        }));
      legend.data.setAll(legendData);
    },
    [styleConfig, seriesConfig],
  );

  useLayoutEffect(() => {
    if (!chartRef.current || !hasData) return;
    const MIN_ROW_HEIGHT = 40;
    const calculatedHeight = Math.max(400, dataWithUniqueKeys.length * MIN_ROW_HEIGHT);
    chartRef.current.style.height = `${calculatedHeight}px`;

    chartRef.current.innerHTML = '';
    if (am5RootRef.current) {
      try {
        am5RootRef.current.dispose();
      } catch {
        /* ignore */
      }
      am5RootRef.current = null;
    }

    const root = am5.Root.new(chartRef.current);
    am5RootRef.current = root;
    root.setThemes([am5themes_Dark.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 30,
        paddingTop: 20,
        paddingBottom: 10,
        background: am5.Rectangle.new(root, { fill: am5.color('#122031'), fillOpacity: 0 }),
      }),
    );

    const { xAxis, yAxis } = createAxis(root, chart);

    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'zoomX',
        xAxis: xAxis,
      }),
    );
    cursor.lineX.set('visible', false);
    cursor.lineY.set('visible', false);
    const allSeries = createSeries(root, chart, xAxis, yAxis);

    createLegend(root, chart, allSeries);

    chart.appear(1000, 100);

    return () => {
      if (am5RootRef.current) {
        try {
          am5RootRef.current.dispose();
        } catch {
          console.error('Error disposing am5 root');
        }
        am5RootRef.current = null;
      }
    };
  }, [hasData, createAxis, createSeries, createLegend, dataWithUniqueKeys.length]);

  return (
    <div className='runlife-main-container' style={{ marginRight: marginRight ? '0px' : '' }}>
      <section
        ref={containerRef}
        className={`ogl-chart-container runlife-bar-chart-container${isFullscreen ? ' fullscreen' : ''}`}
      >
        <header className='ogl-chart-header'>
          <div className='chart-title'>
            <h2>{widgetHeader}</h2>
          </div>
          <div className='chart-actions'>
            <button
              analytics-id='runlife-bar-chart-fullscreen-btn'
              className='chart-action-button'
              onClick={toggleFullscreen}
            >
              <FullScreenIcon />
            </button>
            <button analytics-id='runlife-bar-chart-download-btn' className='chart-action-button'>
              <DownloadChart onClick={handleChartDownload} canExportJpg={true} canExportPdf={true} />
            </button>
          </div>
        </header>
        <div className='chart-divider'></div>
        <div id={chartContentId} className='runlife-bar-chart-content'>
          <div className='runlife-bar-chart-label'>{chartLabel ?? ''}</div>
          {hasData ? <div ref={chartRef} className='chart-area' /> : <EmptyState message='No data available' />}
        </div>
      </section>
    </div>
  );
};

export default RunlifeBarChart;
